import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Zap, Grid3X3, Sun, Car, Home, Shield, Settings, ArrowRight } from 'lucide-react';
import { mockData } from '../mock';

const Services = () => {
  const iconMap = {
    'Zap': Zap,
    'Grid3X3': Grid3X3,
    'Sun': Sun,
    'Car': Car,
    'Home': Home,
    'Shield': Shield,
    'Settings': Settings
  };

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Unsere Leistungen
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Von der klassischen Elektroinstallation bis zur modernen Smart Home Lösung – 
              wir bieten Ihnen das komplette Spektrum der Elektrotechnik.
            </p>
          </div>

          {/* Services Grid - 3 oben, 2 unten zentriert */}
          <div className="mb-16 space-y-8">
            {/* Erste 3 Services */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {mockData.services.slice(0, 3).map((service, index) => {
                const IconComponent = iconMap[service.icon] || Zap;
                return (
                  <Card 
                    key={index} 
                    className="group hover:shadow-2xl transition-all duration-300 border-0 shadow-lg hover:-translate-y-2 h-full flex flex-col"
                  >
                    <CardHeader className="pb-4">
                      <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                        <IconComponent className="h-8 w-8 text-white" />
                      </div>
                      <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                        {service.category}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col">
                      <p className="text-gray-600 mb-6 leading-relaxed">
                        {service.description}
                      </p>
                      <div className="space-y-2 mb-6 flex-1">
                        {service.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center space-x-2">
                            <div className="w-1.5 h-1.5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"></div>
                            <span className="text-gray-700 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                      <Button 
                        onClick={scrollToContact}
                        variant="ghost" 
                        className="w-full text-blue-600 hover:text-white hover:bg-gradient-to-r hover:from-cyan-500 hover:to-blue-500 transition-all duration-300 group mt-auto"
                      >
                        Anfrage stellen
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            {/* Letzte 2 Services - zentriert */}
            <div className="flex justify-center">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl">
                {mockData.services.slice(3).map((service, index) => {
                  const IconComponent = iconMap[service.icon] || Zap;
                  return (
                    <Card 
                      key={index + 3} 
                      className="group hover:shadow-2xl transition-all duration-300 border-0 shadow-lg hover:-translate-y-2 h-full flex flex-col"
                    >
                      <CardHeader className="pb-4">
                        <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                          <IconComponent className="h-8 w-8 text-white" />
                        </div>
                        <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                          {service.category}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="flex-1 flex flex-col">
                        <p className="text-gray-600 mb-6 leading-relaxed">
                          {service.description}
                        </p>
                        <div className="space-y-2 mb-6 flex-1">
                          {service.features.map((feature, featureIndex) => (
                            <div key={featureIndex} className="flex items-center space-x-2">
                              <div className="w-1.5 h-1.5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"></div>
                              <span className="text-gray-700 text-sm">{feature}</span>
                            </div>
                          ))}
                        </div>
                        <Button 
                          onClick={scrollToContact}
                          variant="ghost" 
                          className="w-full text-blue-600 hover:text-white hover:bg-gradient-to-r hover:from-cyan-500 hover:to-blue-500 transition-all duration-300 group mt-auto"
                        >
                          Anfrage stellen
                          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-[#0a1632] to-[#1a2642] rounded-3xl p-6 md:p-12 text-center text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Brauchen Sie eine individuelle Lösung?
            </h3>
            <p className="text-lg md:text-xl text-white/80 mb-6 md:mb-8 max-w-2xl mx-auto">
              Jedes Projekt ist einzigartig. Lassen Sie uns gemeinsam die perfekte Elektrolösung für Ihre Anforderungen entwickeln.
            </p>
            <Button 
              onClick={scrollToContact}
              size="lg"
              className="w-full sm:w-auto bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white border-0 px-6 sm:px-8 py-4 text-base sm:text-lg font-semibold shadow-xl transition-all duration-300 hover:shadow-cyan-500/25 hover:scale-105"
            >
              Kostenlose Beratung sichern
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;